import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useSettings } from "@/lib/settings";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import LeadForm from "@/components/LeadForm";

function Icon({ name, className = "w-6 h-6" }) {
  const Ico = Icons[name] || Icons.Sparkles;
  return <Ico className={className} />;
}

export default function ServicesPage() {
  const { settings } = useSettings();
  const [services, setServices] = useState([]);
  const [courses, setCourses] = useState([]);
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    api.get("/services").then(({ data }) => setServices(data));
    api.get("/courses").then(({ data }) => setCourses(data));
    api.get("/testimonials").then(({ data }) => setTestimonials(data)).catch(() => {});
  }, []);

  const cfg = settings?.services_page || {};

  const linkOrHref = (link, className, testId, text, extraIcon) => {
    if (!text) return null;
    if (link?.startsWith("http")) return <a href={link} target="_blank" rel="noreferrer" className={className} data-testid={testId}>{text} {extraIcon}</a>;
    return <Link to={link || "/contact"} className={className} data-testid={testId}>{text} {extraIcon}</Link>;
  };

  return (
    <div className="pb-24" data-testid="services-page">
      {/* Hero */}
      <section className="relative py-20 lg:py-28 bg-gradient-to-br from-neutral-950 to-neutral-900 text-white overflow-hidden" data-testid="services-hero">
        {cfg.hero_image && (
          <img src={cfg.hero_image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        )}
        <div className="container-eq relative">
          <div className="max-w-3xl">
            {cfg.hero_kicker && <div className="label-eq text-sky-400 mb-3">{cfg.hero_kicker}</div>}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-black tracking-tighter" data-testid="services-title">{cfg.hero_title || "What we offer"}</h1>
            {cfg.hero_subtitle && <p className="mt-5 text-lg text-neutral-300 leading-relaxed max-w-2xl">{cfg.hero_subtitle}</p>}
            {cfg.hero_cta_visible !== false && cfg.hero_cta_text && (
              linkOrHref(cfg.hero_cta_link, "mt-8 inline-flex items-center gap-2 bg-white text-neutral-950 px-6 py-3 rounded-full font-semibold hover:scale-105 transition-transform", "services-hero-cta", cfg.hero_cta_text)
            )}
          </div>
        </div>
      </section>

      {/* Intro */}
      {cfg.intro_visible !== false && (cfg.intro_heading || cfg.intro_body) && (
        <section className="py-16 container-eq" data-testid="services-intro">
          <div className="max-w-3xl">
            {cfg.intro_kicker && <div className="label-eq text-sky-500 mb-3">{cfg.intro_kicker}</div>}
            {cfg.intro_heading && <h2 className="text-3xl sm:text-4xl font-display font-black tracking-tight">{cfg.intro_heading}</h2>}
            {cfg.intro_body && <p className="mt-4 text-neutral-600 leading-relaxed text-lg">{cfg.intro_body}</p>}
          </div>
        </section>
      )}

      {/* Services grid */}
      <section className="py-16 container-eq" data-testid="services-grid">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((s) => {
            const linked = (s.related_course_slugs || []).map((slug) => courses.find((c) => c.slug === slug)).filter(Boolean);
            return (
              <div key={s.id} className="p-8 card-eq relative overflow-hidden group" data-testid={`service-${s.slug}`}>
                {s.featured && (
                  <div className="absolute top-4 right-4 text-yellow-500 text-xs font-bold uppercase tracking-wider">★ Featured</div>
                )}
                {s.image ? (
                  <img src={s.image} alt={s.title} className="w-14 h-14 rounded-xl object-cover mb-4" />
                ) : (
                  <div className="w-12 h-12 rounded-xl bg-neutral-950 flex items-center justify-center mb-4">
                    <Icon name={s.icon} className="w-6 h-6 text-sky-400" />
                  </div>
                )}
                <div className="font-display font-bold text-xl">{s.title}</div>
                <p className="mt-2 text-neutral-600 text-sm leading-relaxed">{s.short_description}</p>
                {s.features?.length > 0 && (
                  <ul className="mt-4 space-y-1.5">
                    {s.features.map((f, i) => (
                      <li key={i} className="text-xs text-neutral-500 flex items-start gap-1.5">
                        <span className="w-1 h-1 rounded-full bg-sky-500 mt-1.5 shrink-0"></span>{f}
                      </li>
                    ))}
                  </ul>
                )}
                {linked.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-neutral-100">
                    <div className="text-[10px] uppercase tracking-widest text-neutral-400 font-semibold">Related courses</div>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {linked.map((c) => (
                        <Link key={c.id} to={`/courses/${c.slug}`} className="text-xs px-2 py-1 rounded-full bg-sky-50 border border-sky-100 text-sky-700 hover:bg-sky-100" data-testid={`service-related-${c.slug}`}>{c.title}</Link>
                      ))}
                    </div>
                  </div>
                )}
                {s.button_visible !== false && s.button_text && (
                  linkOrHref(s.button_link, "mt-5 inline-flex text-sm font-semibold text-sky-600 hover:text-sky-700", `service-cta-${s.slug}`, s.button_text, "→")
                )}
              </div>
            );
          })}
        </div>
        {services.length === 0 && <div className="text-center text-neutral-500 py-16">No services published yet.</div>}
      </section>

      {/* Process */}
      {cfg.process_visible !== false && cfg.process_steps?.length > 0 && (
        <section className="py-16 bg-neutral-50" data-testid="services-process">
          <div className="container-eq">
            <h2 className="text-3xl sm:text-4xl font-display font-black tracking-tight text-center mb-12">{cfg.process_heading || "How we work"}</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {cfg.process_steps.map((step, i) => (
                <div key={i} className="relative p-6 rounded-2xl bg-white border border-neutral-200" data-testid={`process-step-${i}`}>
                  <div className="absolute -top-4 -left-2 w-10 h-10 rounded-full bg-neutral-950 text-white flex items-center justify-center font-display font-bold text-sm">{i + 1}</div>
                  <div className="mt-2 w-11 h-11 rounded-xl bg-sky-100 flex items-center justify-center mb-4">
                    <Icon name={step.icon} className="w-5 h-5 text-sky-600" />
                  </div>
                  <div className="font-display font-bold text-lg">{step.title}</div>
                  <p className="mt-2 text-sm text-neutral-600 leading-relaxed">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Stats */}
      {cfg.stats_visible !== false && cfg.stats?.length > 0 && (
        <section className="py-16 bg-neutral-950 text-white" data-testid="services-stats">
          <div className="container-eq">
            {cfg.stats_heading && <h2 className="text-3xl sm:text-4xl font-display font-black tracking-tight text-center mb-12">{cfg.stats_heading}</h2>}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {cfg.stats.map((st, i) => (
                <div key={i} className="text-center" data-testid={`stat-${i}`}>
                  <div className="inline-flex w-12 h-12 rounded-xl bg-sky-500/10 items-center justify-center mb-3">
                    <Icon name={st.icon} className="w-5 h-5 text-sky-400" />
                  </div>
                  <div className="text-4xl sm:text-5xl font-display font-black tracking-tighter text-white">{st.metric}</div>
                  <div className="mt-2 text-sm text-neutral-400">{st.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Highlights */}
      {cfg.highlights_visible !== false && cfg.highlights?.length > 0 && (
        <section className="py-16" data-testid="services-highlights">
          <div className="container-eq">
            <h2 className="text-3xl sm:text-4xl font-display font-black tracking-tight text-center mb-12">{cfg.highlights_heading || "Why students choose us"}</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {cfg.highlights.map((h, i) => (
                <div key={i} className="p-8 card-eq" data-testid={`highlight-${i}`}>
                  <div className="w-12 h-12 rounded-xl bg-sky-500 flex items-center justify-center mb-4 text-white">
                    <Icon name={h.icon} className="w-5 h-5" />
                  </div>
                  <div className="font-display font-bold text-xl">{h.title}</div>
                  <p className="mt-2 text-neutral-600 text-sm leading-relaxed">{h.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Testimonials */}
      {cfg.testimonials_visible !== false && testimonials.length > 0 && (
        <section className="py-16 bg-neutral-50" data-testid="services-testimonials">
          <div className="container-eq">
            <h2 className="text-3xl sm:text-4xl font-display font-black tracking-tight text-center mb-12">{cfg.testimonials_heading || "What our students say"}</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {testimonials.slice(0, 6).map((t, i) => (
                <div key={t.id || i} className="p-6 card-eq" data-testid={`services-testimonial-${i}`}>
                  <div className="flex items-center gap-3">
                    {t.image && <img src={t.image} alt={t.name} className="w-11 h-11 rounded-full object-cover" />}
                    <div>
                      <div className="font-semibold text-sm">{t.name}</div>
                      <div className="text-xs text-neutral-500">{t.role || t.course}</div>
                    </div>
                  </div>
                  <p className="mt-4 text-sm text-neutral-700 leading-relaxed">"{t.review}"</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQs */}
      {cfg.faqs_visible !== false && cfg.faqs?.length > 0 && (
        <section className="py-16 bg-white" data-testid="services-faqs">
          <div className="container-eq max-w-3xl">
            <h2 className="text-3xl font-display font-bold tracking-tight mb-8 text-center">{cfg.faqs_heading || "Common questions"}</h2>
            <div className="space-y-3">
              {cfg.faqs.map((f, i) => (
                <details key={i} className="border border-neutral-200 rounded-2xl overflow-hidden" data-testid={`services-faq-${i}`}>
                  <summary className="cursor-pointer px-5 py-4 font-semibold hover:bg-neutral-50">{f.q}</summary>
                  <div className="px-5 pb-4 text-sm text-neutral-600 leading-relaxed">{f.a}</div>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      {cfg.cta_visible !== false && (
        <section className="py-16 bg-neutral-950 text-white relative overflow-hidden" data-testid="services-cta">
          {cfg.cta_background_image && (
            <img src={cfg.cta_background_image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-15" />
          )}
          <div className="container-eq grid lg:grid-cols-2 gap-10 items-center relative">
            <div>
              <h2 className="text-3xl sm:text-4xl font-display font-bold tracking-tight leading-tight">{cfg.cta_heading || "Talk to us"}</h2>
              <p className="text-neutral-400 mt-3 leading-relaxed">{cfg.cta_body || "Our counselors will help you pick the right service."}</p>
              {cfg.cta_button_text && (
                linkOrHref(cfg.cta_button_link, "btn-primary-eq mt-6", "services-cta-button", cfg.cta_button_text)
              )}
            </div>
            <div className="bg-white text-neutral-900 rounded-3xl p-6">
              <LeadForm source="services_cta" compact />
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
